import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Crosshair, Lock, Minus, Plus, Type, Unlock } from "lucide-react";

import type { MeasurementType, ToolType } from "./registry";
import {
  MEASUREMENT_INCREMENT_OPTIONS,
  MEASUREMENT_TYPES_BY_TOOL,
  type MeasurementToolDefinition,
} from "./registry";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { parseFeetInches } from "@/lib/geometry";
import { ViewportToolsetSubButton } from "@/viewport-tools/core";
import { DEFAULT_TEXT_FONT_SIZE } from "@/hooks/useMeasurementTextLock";
import { cn } from "@/lib/utils";

interface MeasurementToolsetPanelProps {
  measurementTools: MeasurementToolDefinition[];
  activeTool: ToolType | null;
  activeMeasurementType: MeasurementType | null;
  onToolSelect: (tool: ToolType) => void;
  onTypeSelect: (type: MeasurementType) => void;
  snapEnabled: boolean;
  onSnapToggle: () => void;
  incrementSnapEnabled: boolean;
  onIncrementSnapToggle: () => void;
  incrementStepFeet: number;
  onIncrementStepChange: (stepFeet: number) => void;
  showHeightInput: boolean;
  heightValue: string;
  onHeightChange: (value: string) => void;
  textLocked?: boolean;
  onTextLockToggle?: () => void;
  textFontSize?: number;
  onTextFontSizeChange?: (value: number) => void;
}

interface ToolSettingsPanelState {
  kind: "tool-settings";
}

interface AnnotationsPanelState {
  kind: "annotations";
}

type SecondaryPanelState = ToolSettingsPanelState | AnnotationsPanelState | null;

const SECONDARY_PANEL_BASE =
  "pointer-events-auto absolute left-full ml-1 z-10 w-44 rounded-md border border-border/70 bg-background/95 p-2 shadow-lg backdrop-blur";

function clampFontSize(value: number) {
  return Math.min(72, Math.max(1, Math.round(value * 2) / 2));
}

function measurementTypeRequiresHeight(tool: ToolType | null, measurementType: MeasurementType) {
  return (
    measurementType === "CF" ||
    ((tool === "line" || tool === "polyline") && measurementType === "SQFT")
  );
}

function formatIncrementSummaryLabel(stepFeet: number) {
  const fullLabel =
    MEASUREMENT_INCREMENT_OPTIONS.find((option) => option.stepFeet === stepFeet)?.label ?? `1'-0"`;
  return fullLabel.replace(`'-0"`, `'`);
}

function hasValidHeightValue(heightValue: string) {
  const parsedHeight = parseFeetInches(heightValue);
  return parsedHeight !== null && parsedHeight > 0;
}

export function MeasurementToolsetPanel({
  measurementTools,
  activeTool,
  activeMeasurementType,
  onToolSelect,
  onTypeSelect,
  snapEnabled,
  onSnapToggle,
  incrementSnapEnabled,
  onIncrementSnapToggle,
  incrementStepFeet,
  onIncrementStepChange,
  showHeightInput,
  heightValue,
  onHeightChange,
  textLocked,
  onTextLockToggle,
  textFontSize,
  onTextFontSizeChange,
}: MeasurementToolsetPanelProps) {
  const panelRootRef = useRef<HTMLDivElement | null>(null);
  const toolButtonRefs = useRef<Partial<Record<ToolType, HTMLButtonElement | null>>>({});
  const annotationsButtonRef = useRef<HTMLButtonElement | null>(null);
  const heightInputRef = useRef<HTMLInputElement | null>(null);
  const previousActiveToolRef = useRef<ToolType | null>(activeTool);
  const [secondaryPanel, setSecondaryPanel] = useState<SecondaryPanelState>(
    activeTool ? { kind: "tool-settings" } : null
  );
  const [secondaryPanelTop, setSecondaryPanelTop] = useState("0px");
  const hasAnnotationsControls = Boolean(onTextLockToggle || onTextFontSizeChange);

  useEffect(() => {
    if (previousActiveToolRef.current === activeTool) {
      return;
    }

    previousActiveToolRef.current = activeTool;
    if (activeTool) {
      setSecondaryPanel({ kind: "tool-settings" });
      return;
    }

    setSecondaryPanel((current) =>
      current?.kind === "tool-settings" ? null : current
    );
  }, [activeTool]);

  useEffect(() => {
    if (secondaryPanel?.kind === "tool-settings" && activeTool === null) {
      setSecondaryPanel(null);
      return;
    }

    if (secondaryPanel?.kind === "annotations" && !hasAnnotationsControls) {
      setSecondaryPanel(null);
    }
  }, [activeTool, hasAnnotationsControls, secondaryPanel]);

  useLayoutEffect(() => {
    const updateSecondaryPanelTop = () => {
      const rootRect = panelRootRef.current?.getBoundingClientRect();
      if (!secondaryPanel || !rootRect) {
        setSecondaryPanelTop("0px");
        return;
      }

      const trigger =
        secondaryPanel.kind === "tool-settings"
          ? activeTool
            ? toolButtonRefs.current[activeTool] ?? null
            : null
          : annotationsButtonRef.current;

      const triggerRect = trigger?.getBoundingClientRect();
      if (!triggerRect) {
        setSecondaryPanelTop("0px");
        return;
      }

      setSecondaryPanelTop(`${Math.round(triggerRect.top - rootRect.top)}px`);
    };

    updateSecondaryPanelTop();
    window.addEventListener("resize", updateSecondaryPanelTop);
    return () => window.removeEventListener("resize", updateSecondaryPanelTop);
  }, [measurementTools, secondaryPanel, hasAnnotationsControls]);

  const handleToolSelect = (toolId: ToolType) => {
    const isDeselectingActiveTool = activeTool === toolId;
    onToolSelect(toolId);
    setSecondaryPanel(isDeselectingActiveTool ? null : { kind: "tool-settings" });
  };

  const handleIncrementSelect = (stepFeet: number) => {
    if (!incrementSnapEnabled) {
      onIncrementSnapToggle();
    }
    onIncrementStepChange(stepFeet);
  };

  const currentTextFontSize = textFontSize ?? DEFAULT_TEXT_FONT_SIZE;
  const [fontSizeInput, setFontSizeInput] = useState(String(currentTextFontSize));
  const selectedIncrementSummary = formatIncrementSummaryLabel(incrementStepFeet);

  useEffect(() => {
    setFontSizeInput(String(currentTextFontSize));
  }, [currentTextFontSize]);

  useEffect(() => {
    if (
      secondaryPanel?.kind !== "tool-settings" ||
      !showHeightInput ||
      !hasValidHeightValue(heightValue)
    ) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (panelRootRef.current?.contains(target)) {
        return;
      }

      setSecondaryPanel(null);
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [heightValue, secondaryPanel, showHeightInput]);

  useEffect(() => {
    if (secondaryPanel?.kind !== "tool-settings" || !showHeightInput) {
      return;
    }

    heightInputRef.current?.focus();
    heightInputRef.current?.select();
  }, [secondaryPanel, showHeightInput, activeMeasurementType]);

  return (
    <div ref={panelRootRef} className="relative flex flex-col">
      {measurementTools.map((tool) => {
        const ToolIcon = tool.icon;

        return (
          <ViewportToolsetSubButton
            key={tool.id}
            buttonRef={(element) => {
              toolButtonRefs.current[tool.id] = element;
            }}
            icon={<ToolIcon className="size-4" />}
            label={tool.label}
            sublabel={
              activeTool === tool.id && activeMeasurementType ? (
                <>
                  <span>{activeMeasurementType}</span>
                  <span aria-hidden="true">-</span>
                  <span>{selectedIncrementSummary}</span>
                  {snapEnabled ? <Crosshair className="size-2.5" /> : null}
                </>
              ) : undefined
            }
            sublabelAriaLabel={
              activeTool === tool.id && activeMeasurementType
                ? `${activeMeasurementType}, ${selectedIncrementSummary}${snapEnabled ? ", snap enabled" : ""}`
                : undefined
            }
            active={activeTool === tool.id}
            onClick={() => handleToolSelect(tool.id)}
          />
        );
      })}

      {hasAnnotationsControls ? (
        <ViewportToolsetSubButton
          buttonRef={annotationsButtonRef}
          icon={<Type className="size-4" />}
          label="Annotations"
          buttonClassName="border-b border-border/70"
          active={secondaryPanel?.kind === "annotations"}
          onClick={() =>
            setSecondaryPanel((current) =>
              current?.kind === "annotations" ? null : { kind: "annotations" }
            )
          }
        />
      ) : null}

      {secondaryPanel?.kind === "tool-settings" && activeTool ? (
        <div className={SECONDARY_PANEL_BASE} style={{ top: secondaryPanelTop }}>
          <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            Units
          </div>
          <div className="grid grid-cols-3 gap-1">
            {MEASUREMENT_TYPES_BY_TOOL[activeTool].map((type) => (
              <Button
                key={type}
                type="button"
                variant={activeMeasurementType === type ? "secondary" : "ghost"}
                size="sm"
                className="h-7 px-1.5 text-[10px]"
                onClick={() => {
                  onTypeSelect(type);
                  if (!measurementTypeRequiresHeight(activeTool, type)) {
                    setSecondaryPanel(null);
                  }
                }}
              >
                {type}
              </Button>
            ))}
          </div>
          <div className="mt-2.5">
            <div className="mb-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              Snap
            </div>
            <Button
              type="button"
              variant={snapEnabled ? "secondary" : "ghost"}
              size="sm"
              className="h-7 w-full justify-start rounded-sm px-2 text-[10px]"
              onClick={onSnapToggle}
            >
              <Crosshair className="mr-1.5 size-3" />
              {snapEnabled ? "Enabled" : "Disabled"}
            </Button>
          </div>
          <div className="mt-2.5">
            <div className="mb-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              Increments
            </div>
            <Button
              type="button"
              variant={incrementSnapEnabled ? "secondary" : "ghost"}
              size="sm"
              className="mb-1 h-7 w-full justify-start rounded-sm px-2 text-[10px]"
              onClick={onIncrementSnapToggle}
            >
              {incrementSnapEnabled ? "Enabled" : "Disabled"}
            </Button>
            <div className="grid grid-cols-2 gap-1">
              {MEASUREMENT_INCREMENT_OPTIONS.map((option) => (
                <Button
                  key={option.stepFeet}
                  type="button"
                  variant={incrementStepFeet === option.stepFeet ? "secondary" : "ghost"}
                  size="sm"
                  className="h-7 justify-start rounded-sm px-2 text-[10px]"
                  onClick={() => handleIncrementSelect(option.stepFeet)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
          {showHeightInput ? (
            <div className="mt-2.5">
              <div className="mb-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Height
              </div>
              <Input
                ref={heightInputRef}
                type="text"
                aria-label="Measurement height"
                value={heightValue}
                onChange={(event) => onHeightChange(event.target.value)}
                placeholder={'e.g. 8\'-0"'}
                className="h-7 rounded-sm px-2 text-[10px]"
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {hasAnnotationsControls && secondaryPanel?.kind === "annotations" ? (
        <div className={SECONDARY_PANEL_BASE} style={{ top: secondaryPanelTop }}>
          {onTextLockToggle ? (
            <>
              <div className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Text Lock
              </div>
              <div className="grid grid-cols-2 gap-1">
                <Button
                  type="button"
                  variant={textLocked ? "secondary" : "ghost"}
                  size="sm"
                  className="h-8 gap-1 px-2 text-xs"
                  onClick={() => {
                    if (!textLocked) {
                      onTextLockToggle();
                    }
                  }}
                >
                  <Lock className="size-3.5" />
                  Lock
                </Button>
                <Button
                  type="button"
                  variant={!textLocked ? "secondary" : "ghost"}
                  size="sm"
                  className="h-8 gap-1 px-2 text-xs"
                  onClick={() => {
                    if (textLocked) {
                      onTextLockToggle();
                    }
                  }}
                >
                  <Unlock className="size-3.5" />
                  Unlock
                </Button>
              </div>
            </>
          ) : null}

          {onTextFontSizeChange ? (
            <div className={cn(onTextLockToggle ? "mt-3" : null)}>
              <div className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Font Size
              </div>
              <div className="flex items-stretch gap-1">
                <Input
                  type="number"
                  min={1}
                  max={72}
                  step={0.5}
                  aria-label="Measurement text size"
                  value={fontSizeInput}
                  onChange={(event) => {
                    if (event.target.value === "") {
                      setFontSizeInput("");
                      return;
                    }

                    setFontSizeInput(event.target.value);
                    const parsed = parseFloat(event.target.value);
                    if (!Number.isFinite(parsed) || parsed <= 0) {
                      return;
                    }
                    onTextFontSizeChange(clampFontSize(parsed));
                  }}
                  className="h-8 text-xs"
                />
                <div className="flex flex-col gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    className="h-[15px] w-5"
                    aria-label="Increase measurement text size"
                    onClick={() => {
                      const nextFontSize = clampFontSize(currentTextFontSize + 0.5);
                      setFontSizeInput(String(nextFontSize));
                      onTextFontSizeChange(nextFontSize);
                    }}
                  >
                    <Plus className="size-2.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    className="h-[15px] w-5"
                    aria-label="Decrease measurement text size"
                    onClick={() => {
                      const nextFontSize = clampFontSize(currentTextFontSize - 0.5);
                      setFontSizeInput(String(nextFontSize));
                      onTextFontSizeChange(nextFontSize);
                    }}
                  >
                    <Minus className="size-2.5" />
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
